import { z } from "zod";

import { CalendarDateSchema } from "./common.js";
import { CreateExpenseRequestSchema, ExpenseRowDtoSchema } from "./expenses.js";

/**
 * DTOs for the vehicles context.
 *
 * Same contract role as its siblings: use cases return these, route handlers pass them through
 * unchanged, and `@gladis/api-client` derives its types from the same schemas — so a contract change
 * is a compile error on both sides at once.
 *
 * **Money crosses as a number of EUROS**, per `savings.ts`'s header: `12.5` is twelve euros fifty,
 * never cents and never a string. Totals are summed as integer cents inside the domain and converted
 * once, at this edge.
 *
 * **Dates cross as `YYYY-MM-DD`.** `first_registered_on`, `valued_on` and `disposed_on` are all
 * `date` columns, and turning one into a `Date` shifts it a day behind UTC — which here moves an
 * observation into the previous month on the net worth chart. Read `savings.ts`'s header before
 * changing any of these to a timestamp.
 */

export const VehicleTypeSchema = z.enum([
  "car_petrol",
  "car_diesel",
  "car_electric",
  "bicycle",
  "ebike",
  "motorcycle",
  "other",
]);
export type VehicleTypeValue = z.infer<typeof VehicleTypeSchema>;

/**
 * Declared with all three, accepted as one.
 *
 * `VehicleOwnershipSchema` describes what a stored row may hold; `SupportedVehicleOwnershipSchema`
 * is what a request may send. Only outright ownership is built — a financed vehicle needs a loan
 * balance beside it and a leased one belongs on no balance sheet at all, and neither exists yet.
 *
 * The narrower schema is on the REQUEST side deliberately: a value the schema admits and no use case
 * handles would show a leased car at full value on the net worth chart with no liability beside it.
 * Silently wrong, in exactly the direction this module exists to correct.
 */
export const VehicleOwnershipSchema = z.enum(["owned", "financed", "leased"]);
export const SupportedVehicleOwnershipSchema = z.literal("owned");
export type VehicleOwnershipValue = z.infer<typeof VehicleOwnershipSchema>;

/**
 * `manual` or `koerslijst`. **There is deliberately no third value.**
 *
 * `property_valuations` has `index` and reserves `avm`, and both are legitimate there BECAUSE CBS
 * OBSERVES THE MARKET. Nothing observes a car for free — RDW gives the catalogue price, ANWB
 * Koerslijst is a web form with no public API, Autotelex is dealer-priced — so a third source here
 * could only mean "a figure this application made up".
 */
export const VehicleValuationSourceSchema = z.enum(["manual", "koerslijst"]);
export type VehicleValuationSourceValue = z.infer<typeof VehicleValuationSourceSchema>;

export const VehicleOwnerRefSchema = z.strictObject({
  /** Null for a jointly held vehicle, and for an owner who has left the household. */
  id: z.uuid().nullable(),
  /** Null only when the vehicle is joint. A departed owner keeps the snapshotted name. */
  displayName: z.string().nullable(),
  /**
   * Whether to draw initials or a stand-in. REQUIRED, on 020's rule.
   *
   * `PersonLabel.isPerson` was optional for an hour and defaulting it to `true` drew initials for
   * "Joint" — which renders cleanly, reads as data, and fails no test.
   */
  isPerson: z.boolean(),
});

/**
 * The depreciation figure, or its absence.
 *
 * **This field is nullable and that is the requirement, not a convenience.** Below two observations
 * there is no figure — and `null` is not zero. Zero claims the vehicle held its value exactly;
 * absence says nobody has measured it twice, which is true and different. `dashboard.md`'s standard
 * for an unpriced holding: omitted, never zeroed.
 */
export const VehicleDepreciationDtoSchema = z.strictObject({
  perYearInEuros: z.number(),
  perMonthInEuros: z.number(),
  totalInEuros: z.number(),
  fromValuedOn: CalendarDateSchema,
  toValuedOn: CalendarDateSchema,
});
export type VehicleDepreciationDto = z.infer<typeof VehicleDepreciationDtoSchema>;

/**
 * What a vehicle is worth now against what the household PAID for it (FR-050).
 *
 * ==========================================================================================
 * IT IS NOT DEPRECIATION AND THE TWO ARE NEVER INTERCHANGEABLE
 * ==========================================================================================
 *
 * `VehicleDepreciationDtoSchema` is the rate of change between two OBSERVATIONS — how fast the thing
 * is falling now, in euros a month. This is the whole distance from a TRANSACTION to the present —
 * how the purchase has worked out, in euros.
 *
 * A household that paid €18,500 for a car worth €16,000 on the forecourt lost €2,500 the moment they
 * drove it away. That is true and useful and it is NOT a measurement of how fast the car is falling;
 * averaging it over the months since would present it as one, which is why the two figures are
 * separate fields with separate names on screen.
 *
 * **Null without BOTH halves** — an unknown purchase price or an unvalued vehicle. Zero would claim
 * the vehicle is worth exactly what it cost, a strong and usually false statement.
 */
export const VehicleGainDtoSchema = z.strictObject({
  /** Signed. Negative is the ordinary case for a car and is not an error state. */
  amountInEuros: z.number(),
  /**
   * Signed, one decimal place. **Null when the vehicle was free**, because the question has no
   * answer — a gift now worth €300 has not gained a percentage of anything. The euro figure above
   * still means something, which is why this is nullable rather than a reason to omit the whole
   * object.
   */
  percent: z.number().nullable(),
});
export type VehicleGainDto = z.infer<typeof VehicleGainDtoSchema>;

export const VehicleDisposalDtoSchema = z.strictObject({
  on: CalendarDateSchema,
  priceInEuros: z.number(),
  /**
   * The newest valuation dated on or before the sale, so the two can be shown side by side.
   *
   * Null when the vehicle was never valued — the household sold something it had not estimated, and
   * there is simply nothing to compare against.
   */
  lastValuationInEuros: z.number().nullable(),
});
export type VehicleDisposalDto = z.infer<typeof VehicleDisposalDtoSchema>;

export const VehicleDtoSchema = z.strictObject({
  id: z.uuid(),
  name: z.string().min(1).max(120),
  type: VehicleTypeSchema,
  firstRegisteredOn: CalendarDateSchema,
  ownership: VehicleOwnershipSchema,
  owner: VehicleOwnerRefSchema,

  /** Null when the vehicle has never been valued. Contributes nothing to net worth (FR-015). */
  currentValueInEuros: z.number().nullable(),
  /** The date of the observation in force. Null alongside a null value. */
  valuedOn: CalendarDateSchema.nullable(),
  valuationCount: z.number().int().nonnegative(),
  /** True once the newest observation is more than six months old (FR-024). */
  valuationIsStale: z.boolean(),

  /** Null below two observations. See `VehicleDepreciationDtoSchema`. */
  depreciation: VehicleDepreciationDtoSchema.nullable(),

  /**
   * What the household paid. **Null means not known, and it is not zero.**
   *
   * Required of every vehicle created since 2026-08-22; null only on rows that predate the field, or
   * — deliberately — never, since zero is a legitimate answer for a gift and the two must stay
   * distinguishable.
   *
   * IT IS NOT A VALUATION AND CONTRIBUTES NOTHING TO NET WORTH. A vehicle with a purchase price and
   * no observation is still "not valued": using what was paid as a stand-in would put a figure on
   * the net worth chart that nobody has checked since the day of the sale, which is the invention
   * this whole module refuses.
   */
  purchasePriceInEuros: z.number().nullable(),
  /** Null without both a purchase price and a valuation. See `VehicleGainDtoSchema`. */
  gain: VehicleGainDtoSchema.nullable(),

  /**
   * CASH per month — the linked recurring costs, and nothing else.
   *
   * **Depreciation is never added to this** (FR-018, FR-019). No money left the household when a
   * vehicle lost value, and 019's amendment records what happens when a balance change is put into a
   * flow figure: it "applied a fact about a BALANCE to a figure that is a FLOW, which is the one
   * thing this page is built not to do."
   */
  monthlyCostInEuros: z.number(),
  linkedCostCount: z.number().int().nonnegative(),

  archivedAt: z.iso.datetime().nullable(),
  disposal: VehicleDisposalDtoSchema.nullable(),
});
export type VehicleDto = z.infer<typeof VehicleDtoSchema>;

/**
 * One month of what the household's vehicles were OBSERVED to be worth.
 *
 * ==========================================================================================
 * IT IS A CARRY-FORWARD AND NOT A CURVE, WHICH IS THE WHOLE FEATURE IN ONE FIELD
 * ==========================================================================================
 *
 * A month's figure is the newest observation dated on or before its last day, per vehicle, summed.
 * Nothing is interpolated between two observations and no percentage is ever deducted between them —
 * `docs/vehicles-decisions.md` §4.2 reversed a modelled depreciation curve for the reason that there
 * is no free market-value source for a car, so a curve would restate the household's own last entry
 * with a number stapled to it.
 *
 * The consequence on screen is that this line is FLAT until somebody records something, and that is
 * the honest shape. `vehicles-valuation-stability.test.ts` advances the clock fourteen months and
 * asserts the figure has not moved a cent.
 *
 * A month before the household's first observation is absent rather than zero, for the reason
 * `VehicleDepreciationDtoSchema` gives: zero is a claim, absence is not.
 */
export const VehicleValuePointSchema = z.strictObject({
  /** `YYYY-MM`. */
  month: z.string().regex(/^\d{4}-\d{2}$/),
  valueInEuros: z.number(),
});
export type VehicleValuePoint = z.infer<typeof VehicleValuePointSchema>;

export const VehiclesPageDtoSchema = z.strictObject({
  /** The evaluation date, resolved ONCE for the whole response. */
  asOf: CalendarDateSchema,
  vehicles: z.array(VehicleDtoSchema),
  /**
   * Thirteen months of household vehicle value, oldest first — the same window every other pillar
   * chart is drawn on.
   *
   * IT COSTS NO EXTRA QUERY. `listVehicles` already reads every valuation in the household to answer
   * "what is this worth today"; the series walks the rows it already has. Asking per month would be
   * thirteen statements that grow with the WINDOW rather than with the data, which is the shape
   * `dashboard-scale.test.ts` exists to catch.
   */
  chart: z.array(VehicleValuePointSchema),
});
export type VehiclesPageDto = z.infer<typeof VehiclesPageDtoSchema>;

export const VehicleValuationDtoSchema = z.strictObject({
  id: z.uuid(),
  valuedOn: CalendarDateSchema,
  amountInEuros: z.number(),
  source: VehicleValuationSourceSchema,
  recordedBy: VehicleOwnerRefSchema,
});
export type VehicleValuationDto = z.infer<typeof VehicleValuationDtoSchema>;

export const VehicleValuationsDtoSchema = z.strictObject({
  vehicleId: z.uuid(),
  /**
   * Travels with the list so the client can validate a date BEFORE submitting it, rather than
   * learning the bound from a refusal.
   */
  firstRegisteredOn: CalendarDateSchema,
  /** Newest first. */
  valuations: z.array(VehicleValuationDtoSchema),
  depreciation: VehicleDepreciationDtoSchema.nullable(),
});
export type VehicleValuationsDto = z.infer<typeof VehicleValuationsDtoSchema>;

/**
 * One recurring expense that is a cost of this vehicle.
 *
 * **`ExpenseRowDtoSchema` ITSELF, not a reduced copy of it.**
 *
 * It held five fields until 2026-08-22 — description, category, interval, monthly equivalent and an
 * archived flag — which was enough for a small table of its own and not enough to be the SAME table.
 * The vehicle detail page now renders the expenses list's own rows, with the same columns, the same
 * card and "whose" cells and the same row actions, so it needs the same row.
 *
 * The reduced shape was also missing the two things a member most wants here: which card pays it,
 * and whose cost it is. Reusing the schema is what makes those impossible to forget again.
 */
export const VehicleCostDtoSchema = ExpenseRowDtoSchema;
export type VehicleCostDto = z.infer<typeof VehicleCostDtoSchema>;

/**
 * One vehicle in full — the detail page's whole payload.
 *
 * ==========================================================================================
 * IT SERVES THE PAGE WHERE COSTS ARE ADDED, WHICH IS THE ONE PLACE THEY ARE
 * ==========================================================================================
 *
 * A vehicle's costs are entered "underneath the car" and nowhere else in this module. The list page
 * shows a total and links here; this page shows the rows behind it and the form that adds one.
 *
 * **The rows are still ordinary recurring expenses.** They appear on the Expenses page grouped under
 * the vehicle, and archiving or editing one there behaves exactly as it does for any other row.
 * There is no vehicle-private store of costs, which is what `vehicles-hold-no-costs.test.ts` guards.
 */
export const VehicleDetailDtoSchema = z.strictObject({
  vehicle: VehicleDtoSchema,
  /** Newest first. */
  valuations: z.array(VehicleValuationDtoSchema),
  costs: z.array(VehicleCostDtoSchema),
});
export type VehicleDetailDto = z.infer<typeof VehicleDetailDtoSchema>;

// --- requests ---------------------------------------------------------------

export const CreateVehicleRequestSchema = z.strictObject({
  name: z.string().min(1).max(120),
  type: VehicleTypeSchema,
  firstRegisteredOn: CalendarDateSchema,
  ownerId: z.uuid().nullable(),
  /**
   * What the household paid. **Required**, and `0` is a legitimate answer for a gift.
   *
   * Required here while the COLUMN is nullable, which is not an inconsistency: the column has to
   * represent rows written before the field existed, and backfilling those with a default would
   * state a price nobody paid.
   */
  purchasePriceInEuros: z.number().nonnegative(),
  /**
   * Roughly what it is worth TODAY. **Optional**, and it is not stored on the vehicle.
   *
   * Supplied, it becomes an ordinary `manual` valuation dated today, written by `recordValuation` in
   * the same transaction. That is the whole of its special treatment: one path into
   * `vehicle_valuations`, so the carry-forward, the chart, the staleness prompt and net worth all
   * treat it as the observation it is, with no special case anywhere downstream.
   *
   * Omitted, the vehicle is simply not valued — a legitimate state that contributes nothing to net
   * worth and still groups its costs (FR-015).
   */
  currentValueInEuros: z.number().nonnegative().optional(),
});

/**
 * Editing takes the same fields MINUS the current value.
 *
 * A valuation is recorded, corrected or removed through its own endpoints — offering it again here
 * would make "save" silently append an observation every time somebody fixed a typo in the name.
 */
export const EditVehicleRequestSchema = CreateVehicleRequestSchema.omit({
  currentValueInEuros: true,
});

export const RecordValuationRequestSchema = z.strictObject({
  valuedOn: CalendarDateSchema,
  amountInEuros: z.number().nonnegative(),
  source: VehicleValuationSourceSchema,
});

export const CorrectValuationRequestSchema = RecordValuationRequestSchema;

export const DisposeVehicleRequestSchema = z.strictObject({
  on: CalendarDateSchema,
  priceInEuros: z.number().nonnegative(),
});

/**
 * Linking accepts EITHER an existing expense or a whole new one (FR-026).
 *
 * The second form creates an ordinary recurring expense and links it in one transaction, so a cost
 * added "underneath the car" is indistinguishable from one created on the expenses page and linked
 * afterwards. **Two entry points, one record** — there is no vehicle-private store of costs.
 */
export const LinkCostRequestSchema = z.union([
  z.strictObject({ expenseId: z.uuid() }),
  /**
   * **`CreateExpenseRequestSchema` ITSELF, not a copy of its fields.**
   *
   * This branch held a hand-written six-field object until 2026-08-22, differing from the real one in
   * three ways at once: `amountInEuros` where expenses says `amount`, a required `cardId` where
   * expenses makes it optional, and NO `splits` at all. Every one of those is a place the two could
   * disagree, and the third was already a defect — the form offers a split editor, so a member could
   * enter 60/40 and have it silently dropped on the way to a vehicle.
   *
   * Sharing the schema makes the vehicle path accept exactly what the expenses path accepts, forever,
   * because there is nothing left to keep in step. It is also what lets `ExpenseForm` be rendered
   * unchanged against this endpoint: the body it already assembles IS this object.
   */
  z.strictObject({ expense: CreateExpenseRequestSchema }),
]);
export type LinkCostRequest = z.infer<typeof LinkCostRequestSchema>;

/**
 * A successful sale that carries advice (FR-049).
 *
 * The still-linked costs are reported so the member can end the ones that have genuinely stopped —
 * NOT archived automatically, because a household that sold a car on the 3rd may still be paying its
 * insurance until the policy is cancelled. Silently archiving them would delete real recurring costs
 * the household is still paying.
 */
export const DisposeVehicleResponseSchema = z.strictObject({
  vehicle: VehicleDtoSchema,
  stillLinked: z.array(
    z.strictObject({
      id: z.uuid(),
      description: z.string(),
      monthlyEquivalentInEuros: z.number(),
    }),
  ),
});
export type DisposeVehicleResponse = z.infer<typeof DisposeVehicleResponseSchema>;
